import {
  Download, PieChart, Users, Clock, TrendingUp, Utensils, MapPin, FileSpreadsheet
} from 'lucide-react';
import { getZoneRank } from '../../utils/zoneUtils';
import type { ReportRow, FilterMode } from './types';
import PeriodSelector from './PeriodSelector';

interface PayeTabProps {
  payrollData: ReportRow[];
  periodLabel: string;
  // PeriodSelector props
  filterMode: FilterMode;
  setFilterMode: (mode: FilterMode) => void;
  selectedYear: number;
  setSelectedYear: (year: number) => void;
  selectedMonth: number;
  setSelectedMonth: (month: number) => void;
  selectedWeek: number;
  setSelectedWeek: (week: number) => void;
  handlePrevious: () => void;
  handleNext: () => void;
  handleToday: () => void;
  // Export functions
  downloadPayrollPDF: () => void;
  downloadXLSX: () => void;
}

export default function PayeTab({
  payrollData,
  filterMode, setFilterMode,
  selectedYear, setSelectedYear,
  selectedMonth, setSelectedMonth,
  selectedWeek, setSelectedWeek,
  handlePrevious, handleNext, handleToday,
  periodLabel,
  downloadPayrollPDF, downloadXLSX
}: PayeTabProps) {
  // Calculs paie (arrondi pour éviter les erreurs de virgule flottante)
  const totalHoursAll = parseFloat(payrollData.reduce((acc, r) => acc + r.totalHours, 0).toFixed(2));
  const totalOvertimeAll = parseFloat(payrollData.reduce((acc, r) => acc + r.overtime, 0).toFixed(2));
  const totalOvertime25All = parseFloat(payrollData.reduce((acc, r) => acc + r.overtime25, 0).toFixed(2));
  const totalOvertime50All = parseFloat(payrollData.reduce((acc, r) => acc + r.overtime50, 0).toFixed(2));
  const totalSundayAll = parseFloat(payrollData.reduce((acc, r) => acc + r.sundayHours, 0).toFixed(2));
  const totalMealsAll = payrollData.reduce((acc, r) => acc + r.totalMeals, 0);
  const totalTransportAll = payrollData.reduce((acc, r) => acc + r.totalTransport, 0);
  const totalGDDaysAll = payrollData.reduce((acc, r) => acc + r.gdDaysCount, 0);
  const totalGDAmountAll = parseFloat(payrollData.reduce((acc, r) => acc + r.gdTotalAmount, 0).toFixed(2));
  const totalGDTravelHoursAll = parseFloat(payrollData.reduce((acc, r) => acc + r.gdTotalTravelHours, 0).toFixed(2));

  const periodSelectorProps = {
    filterMode, setFilterMode,
    selectedYear, setSelectedYear,
    selectedMonth, setSelectedMonth,
    selectedWeek, setSelectedWeek,
    handlePrevious, handleNext, handleToday,
    periodLabel
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      {/* Sélecteur de période */}
      <PeriodSelector {...periodSelectorProps} />

      {/* Stats Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '16px' }}>
        <div style={{
          background: 'white', borderRadius: '16px', padding: '20px',
          border: '1px solid #e2e8f0', boxShadow: '0 2px 4px rgba(0,0,0,0.02)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
            <div style={{ padding: '8px', background: '#eff6ff', borderRadius: '8px', color: '#2563eb' }}>
              <Users size={18} />
            </div>
            <span style={{ fontSize: '0.75rem', fontWeight: '600', color: '#64748b', textTransform: 'uppercase' }}>Employés</span>
          </div>
          <div style={{ fontSize: '2rem', fontWeight: '800', color: '#1e293b' }}>{payrollData.length}</div>
          <div style={{ fontSize: '0.8rem', color: '#94a3b8' }}>collaborateurs</div>
        </div>

        <div style={{
          background: 'linear-gradient(135deg, #2563eb 0%, #4f46e5 100%)',
          borderRadius: '16px', padding: '20px', color: 'white',
          boxShadow: '0 4px 6px -1px rgba(37, 99, 235, 0.3)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
            <Clock size={18} />
            <span style={{ fontSize: '0.75rem', fontWeight: '600', textTransform: 'uppercase', opacity: 0.9 }}>Heures Totales</span>
          </div>
          <div style={{ fontSize: '2rem', fontWeight: '800' }}>{totalHoursAll.toFixed(2)}</div>
          <div style={{ fontSize: '0.8rem', opacity: 0.8 }}>heures travaillées</div>
        </div>

        <div style={{
          background: 'white', borderRadius: '16px', padding: '20px',
          border: '1px solid #fde68a', boxShadow: '0 2px 4px rgba(0,0,0,0.02)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
            <div style={{ padding: '8px', background: '#fef3c7', borderRadius: '8px', color: '#d97706' }}>
              <TrendingUp size={18} />
            </div>
            <span style={{ fontSize: '0.75rem', fontWeight: '600', color: '#b45309', textTransform: 'uppercase' }}>H. Supp</span>
          </div>
          <div style={{ fontSize: '2rem', fontWeight: '800', color: '#d97706' }}>
            {(totalOvertimeAll + totalSundayAll) > 0 ? `+${(totalOvertimeAll + totalSundayAll).toFixed(2)}` : '0'}
          </div>
          <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '6px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            {totalOvertime25All > 0 && <span style={{ background: '#fef3c7', padding: '2px 8px', borderRadius: '4px', color: '#92400e' }}>{totalOvertime25All.toFixed(2)}h à 25%</span>}
            {totalOvertime50All > 0 && <span style={{ background: '#fee2e2', padding: '2px 8px', borderRadius: '4px', color: '#991b1b' }}>{totalOvertime50All.toFixed(2)}h à 50%</span>}
            {totalSundayAll > 0 && <span style={{ background: '#dbeafe', padding: '2px 8px', borderRadius: '4px', color: '#1e40af' }}>{totalSundayAll.toFixed(2)}h dim.</span>}
          </div>
        </div>

        <div style={{
          background: 'white', borderRadius: '16px', padding: '20px',
          border: '1px solid #bfdbfe', boxShadow: '0 2px 4px rgba(0,0,0,0.02)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
            <div style={{ padding: '8px', background: '#dbeafe', borderRadius: '8px', color: '#2563eb' }}>
              <Utensils size={18} />
            </div>
            <span style={{ fontSize: '0.75rem', fontWeight: '600', color: '#1d4ed8', textTransform: 'uppercase' }}>Paniers</span>
          </div>
          <div style={{ fontSize: '2rem', fontWeight: '800', color: '#2563eb' }}>{totalMealsAll}</div>
          <div style={{ fontSize: '0.8rem', color: '#94a3b8' }}>repas comptabilisés</div>
        </div>

        {/* Carte Grand Déplacement */}
        <div style={{
          background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
          borderRadius: '16px', padding: '20px', color: 'white',
          boxShadow: '0 4px 6px -1px rgba(220, 38, 38, 0.3)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
            <MapPin size={18} />
            <span style={{ fontSize: '0.75rem', fontWeight: '600', textTransform: 'uppercase', opacity: 0.9 }}>Grands Dépl.</span>
          </div>
          <div style={{ fontSize: '2rem', fontWeight: '800' }}>{totalGDDaysAll}</div>
          <div style={{ fontSize: '0.75rem', opacity: 0.85, marginTop: '4px' }}>
            {totalGDDaysAll > 0 && (
              <span style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <span style={{ background: 'rgba(255,255,255,0.2)', padding: '2px 8px', borderRadius: '4px' }}>{totalGDAmountAll.toFixed(2)} €</span>
                {totalGDTravelHoursAll > 0 && <span style={{ background: 'rgba(255,255,255,0.2)', padding: '2px 8px', borderRadius: '4px' }}>{totalGDTravelHoursAll}h route</span>}
              </span>
            )}
            {totalGDDaysAll === 0 && <span>jours de découché</span>}
          </div>
        </div>
      </div>

      {/* Actions export */}
      <div style={{
        background: 'white', padding: '16px 20px', borderRadius: '12px', border: '1px solid #e2e8f0',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', color: '#64748b', fontSize: '0.9rem' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <MapPin size={16} /> Transports: <strong style={{ color: '#1e293b' }}>{totalTransportAll}</strong>
          </span>
        </div>

        <div style={{ display: 'flex', gap: '10px' }}>
          <button
            onClick={downloadPayrollPDF}
            disabled={payrollData.length === 0}
            style={{
              padding: '12px 24px',
              background: payrollData.length === 0 ? '#f1f5f9' : 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
              color: payrollData.length === 0 ? '#94a3b8' : 'white',
              border: 'none', borderRadius: '10px',
              cursor: payrollData.length === 0 ? 'not-allowed' : 'pointer',
              fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px',
              boxShadow: payrollData.length > 0 ? '0 4px 6px -1px rgba(37, 99, 235, 0.3)' : 'none'
            }}
          >
            <Download size={18} /> Exporter PDF
          </button>
          <button
            onClick={downloadXLSX}
            disabled={payrollData.length === 0}
            style={{
              padding: '12px 24px',
              background: payrollData.length === 0 ? '#f1f5f9' : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              color: payrollData.length === 0 ? '#94a3b8' : 'white',
              border: 'none', borderRadius: '10px',
              cursor: payrollData.length === 0 ? 'not-allowed' : 'pointer',
              fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px',
              boxShadow: payrollData.length > 0 ? '0 4px 6px -1px rgba(16, 185, 129, 0.3)' : 'none'
            }}
          >
            <FileSpreadsheet size={18} /> Exporter Excel (.xlsx)
          </button>
        </div>
      </div>

      {/* Tableau */}
      <div style={{
        background: 'white', borderRadius: '16px', border: '1px solid #e2e8f0',
        overflow: 'hidden', boxShadow: '0 2px 4px rgba(0,0,0,0.02)'
      }}>
        {payrollData.length === 0 ? (
          <div style={{ padding: '80px', textAlign: 'center', color: '#64748b' }}>
            <PieChart size={48} style={{ marginBottom: '15px', opacity: 0.3 }} />
            <p style={{ margin: 0, fontWeight: '600' }}>Aucune donnée validée</p>
            <p style={{ margin: '8px 0 0', fontSize: '0.9rem', color: '#94a3b8' }}>Aucune feuille validée sur cette période</p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
              <thead style={{ background: '#f8fafc' }}>
                <tr>
                  <th style={{ padding: '14px 20px', textAlign: 'left', fontWeight: '600', color: '#475569', fontSize: '0.75rem', textTransform: 'uppercase' }}>Collaborateur</th>
                  <th style={{ padding: '14px 16px', textAlign: 'right', fontWeight: '600', color: '#475569', fontSize: '0.75rem', textTransform: 'uppercase' }}>Heures</th>
                  <th style={{ padding: '14px 16px', textAlign: 'right', fontWeight: '600', color: '#b45309', fontSize: '0.75rem', textTransform: 'uppercase' }}>H. Supp</th>
                  <th style={{ padding: '14px 16px', textAlign: 'right', fontWeight: '600', color: '#2563eb', fontSize: '0.75rem', textTransform: 'uppercase' }}>Paniers</th>
                  <th style={{ padding: '14px 16px', textAlign: 'right', fontWeight: '600', color: '#0891b2', fontSize: '0.75rem', textTransform: 'uppercase' }}>Transp.</th>
                  <th style={{ padding: '14px 16px', textAlign: 'right', fontWeight: '600', color: '#0891b2', fontSize: '0.75rem', textTransform: 'uppercase' }}>Trajets</th>
                  <th style={{ padding: '14px 16px', textAlign: 'center', fontWeight: '600', color: '#dc2626', fontSize: '0.75rem', textTransform: 'uppercase' }}>G. Dépl.</th>
                  <th style={{ padding: '14px 20px', textAlign: 'left', fontWeight: '600', color: '#7c3aed', fontSize: '0.75rem', textTransform: 'uppercase' }}>Zones</th>
                </tr>
              </thead>
              <tbody>
                {payrollData.map((row, idx) => (
                  <tr
                    key={row.employeeId}
                    style={{ borderBottom: idx < payrollData.length - 1 ? '1px solid #f1f5f9' : 'none' }}
                  >
                    <td style={{ padding: '16px 20px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{
                          width: '36px', height: '36px', borderRadius: '10px',
                          background: 'linear-gradient(135deg, #e0e7ff 0%, #c7d2fe 100%)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontWeight: '700', color: '#4f46e5', fontSize: '0.85rem'
                        }}>
                          {row.employeeName.split(' ').map(n => n[0]).join('').slice(0, 2)}
                        </div>
                        <div>
                          <div style={{ fontWeight: '600', color: '#1e293b' }}>{row.employeeName}</div>
                          <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{row.role}</div>
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: '16px', textAlign: 'right' }}>
                      <span style={{ fontWeight: '700', fontSize: '1rem', color: '#1e293b' }}>{row.totalHours.toFixed(2)}</span>
                      <span style={{ color: '#94a3b8', fontSize: '0.8rem', marginLeft: '2px' }}>h</span>
                    </td>
                    <td style={{ padding: '16px', textAlign: 'right' }}>
                      {(row.overtime > 0 || row.sundayHours > 0) ? (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '2px' }}>
                          <span style={{
                            background: '#fef3c7', color: '#b45309',
                            padding: '4px 10px', borderRadius: '6px',
                            fontWeight: '600', fontSize: '0.85rem'
                          }}>
                            +{(row.overtime + row.sundayHours).toFixed(2)}h
                          </span>
                          <div style={{ display: 'flex', gap: '6px', fontSize: '0.7rem' }}>
                            {row.overtime25 > 0 && <span style={{ color: '#92400e' }}>{row.overtime25.toFixed(2)}h 25%</span>}
                            {row.overtime50 > 0 && <span style={{ color: '#991b1b' }}>{row.overtime50.toFixed(2)}h 50%</span>}
                            {row.sundayHours > 0 && <span style={{ color: '#1e40af' }}>{row.sundayHours.toFixed(2)}h dim</span>}
                          </div>
                        </div>
                      ) : (
                        <span style={{ color: '#cbd5e1' }}>-</span>
                      )}
                    </td>
                    <td style={{ padding: '16px', textAlign: 'right' }}>
                      <span style={{ fontWeight: '700', color: '#2563eb' }}>{row.totalMeals}</span>
                    </td>
                    <td style={{ padding: '16px', textAlign: 'right', color: '#475569', fontWeight: '500' }}>{row.totalTransport}</td>
                    <td style={{ padding: '16px', textAlign: 'right', color: '#475569', fontWeight: '500' }}>{row.totalTrajet}</td>
                    <td style={{ padding: '16px', textAlign: 'center' }}>
                      {row.gdDaysCount > 0 ? (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
                          <span style={{
                            background: '#fef2f2', color: '#dc2626',
                            padding: '4px 10px', borderRadius: '6px',
                            fontWeight: '700', fontSize: '0.9rem'
                          }}>
                            {row.gdDaysCount} j
                          </span>
                          <div style={{ fontSize: '0.7rem', color: '#b91c1c' }}>
                            {row.gdTotalAmount > 0 && <span>{row.gdTotalAmount.toFixed(2)}€</span>}
                            {row.gdTotalTravelHours > 0 && <span> · {row.gdTotalTravelHours}h</span>}
                          </div>
                        </div>
                      ) : (
                        <span style={{ color: '#cbd5e1' }}>-</span>
                      )}
                    </td>
                    <td style={{ padding: '16px 20px' }}>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                        {Object.entries(row.zones)
                          .filter(([, count]) => count > 0)
                          .sort((a, b) => getZoneRank(a[0]) - getZoneRank(b[0]))
                          .map(([z, count]) => (
                            <span key={z} style={{
                              padding: '3px 8px', background: '#f3e8ff',
                              borderRadius: '6px', fontSize: '0.75rem',
                              fontWeight: '600', color: '#7c3aed'
                            }}>
                              Z{z}: {count}
                            </span>
                          ))}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
